import React, { useRef, KeyboardEvent } from 'react';
import { InputContainer, InputLabel, InputField, InputControlButton } from './InputBase';

interface DatePickerProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  id?: string;
  minDate?: string;
  maxDate?: string;
  className?: string;
}

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M13.3333 2.66666H12V1.33333C12 0.966659 11.7 0.666656 11.3333 0.666656C10.9667 0.666656 10.6667 0.966659 10.6667 1.33333V2.66666H5.33333V1.33333C5.33333 0.966659 5.03333 0.666656 4.66667 0.666656C4.3 0.666656 4 0.966659 4 1.33333V2.66666H2.66667C1.93333 2.66666 1.33333 3.26666 1.33333 3.99999V13.3333C1.33333 14.0667 1.93333 14.6667 2.66667 14.6667H13.3333C14.0667 14.6667 14.6667 14.0667 14.6667 13.3333V3.99999C14.6667 3.26666 14.0667 2.66666 13.3333 2.66666ZM13.3333 13.3333H2.66667V6.66666H13.3333V13.3333ZM13.3333 5.33333H2.66667V3.99999H13.3333V5.33333Z"
      fill="currentColor"
    />
  </svg>
);

// Format date from YYYY-MM-DD to a more readable format
const formatDisplayDate = (dateString: string): string => {
  if (!dateString) return '';

  try {
    const [year, month, day] = dateString.split('-').map(Number);
    if (!year || !month || !day) return '';

    const date = new Date(year, month - 1, day);
    if (isNaN(date.getTime())) return '';

    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

// Helper function to format date to YYYY-MM-DD string
const formatDateToString = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper to check if date is within min/max range
const isDateInRange = (date: Date, minDate?: string, maxDate?: string): boolean => {
  if (minDate) {
    const min = new Date(minDate);
    min.setHours(0, 0, 0, 0); // Compare dates only
    if (date < min) return false;
  }

  if (maxDate) {
    const max = new Date(maxDate);
    max.setHours(0, 0, 0, 0); // Compare dates only
    if (date > max) return false;
  }

  return true;
};

const DatePicker: React.FC<DatePickerProps> = ({
  value,
  onChange,
  label,
  id = 'date-picker',
  minDate,
  maxDate,
  className,
}) => {
  const displayValue = formatDisplayDate(value);
  const dateInputRef = useRef<HTMLInputElement>(null);
  const displayInputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
  };

  // Open the native date picker when clicking the calendar icon
  const handleCalendarClick = () => {
    if (dateInputRef.current) {
      dateInputRef.current.showPicker();
    }
  };

  // Handle keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!value) {
      // If no date is set, initialize with today's date on arrow press
      if (
        e.key === 'ArrowRight' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown'
      ) {
        const today = new Date();
        if (isDateInRange(today, minDate, maxDate)) {
          onChange(formatDateToString(today));
        }
        return;
      }
      return;
    }

    // Parse the current date
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return;

    const currentDate = new Date(year, month - 1, day);
    const newDate = new Date(currentDate);

    switch (e.key) {
      case 'ArrowRight':
        // Move one day forward
        newDate.setDate(currentDate.getDate() + 1);
        break;
      case 'ArrowLeft':
        // Move one day backward
        newDate.setDate(currentDate.getDate() - 1);
        break;
      case 'ArrowUp':
        // Move one week backward
        newDate.setDate(currentDate.getDate() - 7);
        break;
      case 'ArrowDown':
        // Move one week forward
        newDate.setDate(currentDate.getDate() + 7);
        break;
      default:
        return; // Ignore other keys
    }

    // Only update if the new date is within range
    if (isDateInRange(newDate, minDate, maxDate)) {
      const newDateString = formatDateToString(newDate);
      onChange(newDateString);
      e.preventDefault(); // Prevent default behavior like cursor movement
    }
  };

  return (
    <InputContainer className={className}>
      <InputLabel label={label} htmlFor={id} />
      <div className="flex w-full items-stretch" ref={wrapperRef}>
        <InputField
          id={id}
          type="text"
          value={displayValue}
          onClick={handleCalendarClick}
          onFocus={handleCalendarClick}
          onKeyDown={handleKeyDown}
          onChange={() => {}}
          hasRightControls={true}
          ref={displayInputRef}
          className="cursor-pointer"
          ariaLabel={label}
        />
        <input
          ref={dateInputRef}
          type="date"
          value={value || ''}
          onChange={handleInputChange}
          min={minDate}
          max={maxDate}
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            opacity: 0,
            pointerEvents: 'none',
            clip: 'rect(0 0 0 0)',
            overflow: 'hidden',
          }}
        />
        <InputControlButton
          onClick={handleCalendarClick}
          isRightmost={true}
          className="w-input-height p-0"
        >
          <CalendarIcon />
        </InputControlButton>
      </div>
    </InputContainer>
  );
};

export default DatePicker;
